import { get } from "svelte/store";
import { accessTokenStore } from "../../../stores";
import type {
  TCreateSubscriptionRequest,
  TSubscription,
  TSubscriptionListParams,
  TSubscriptionListResponse,
} from "../../types";

const buildHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${get(accessTokenStore)}`,
});

const buildQueryString = (params: TSubscriptionListParams = {}) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) query.set(key, String(value));
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : "";
};

const parseResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const message = await res.text().catch(() => "");
    throw new Error(
      message || `Subscription request failed with status ${res.status}`,
    );
  }

  return res.json();
};

export const normalizeSubscriptionsResponse = (
  data: TSubscriptionListResponse,
): TSubscription[] => {
  if (Array.isArray(data)) return data;

  return data.items || data.data || data.subscriptions || [];
};

export const createSubscription = async (
  apiUrl: string,
  data: TCreateSubscriptionRequest,
  idempotencyKey?: string,
): Promise<TSubscription> => {
  const res = await fetch(`${apiUrl}/subscriptions`, {
    method: "POST",
    headers: {
      ...buildHeaders(),
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    },
    body: JSON.stringify(data),
  });

  return parseResponse<TSubscription>(res);
};

export const getUserSubscriptions = async (
  apiUrl: string,
  params?: TSubscriptionListParams,
): Promise<TSubscriptionListResponse> => {
  const res = await fetch(
    `${apiUrl}/subscriptions${buildQueryString(params)}`,
    {
      method: "GET",
      headers: buildHeaders(),
    },
  );

  return parseResponse<TSubscriptionListResponse>(res);
};

export const getSubscription = async (
  apiUrl: string,
  id: string,
): Promise<TSubscription> => {
  const res = await fetch(`${apiUrl}/subscriptions/${id}`, {
    method: "GET",
    headers: buildHeaders(),
  });

  return parseResponse<TSubscription>(res);
};

export const cancelSubscription = async (
  apiUrl: string,
  id: string,
): Promise<TSubscription> => {
  const res = await fetch(`${apiUrl}/subscriptions/cancel/${id}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${get(accessTokenStore)}`,
    },
  });

  return parseResponse<TSubscription>(res);
};
